/**
 * ContentContext
 * ══════════════
 * Manages Training Articles and News.
 * Seed data lives in src/data/blogContent.js
 * Admin edits are persisted to localStorage.
 *
 * Keys: "db_blog_articles", "db_blog_news"
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { SEED_ARTICLES, SEED_NEWS, CATEGORIES, NEWS_TYPES } from '../data/blogContent';

const LS_ARTICLES = 'db_blog_articles';
const LS_NEWS     = 'db_blog_news';

const load = (key, fallback) => {
  try {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : fallback;
  } catch { return fallback; }
};

const save = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
};

// Auto-generate slug from title
export const toSlug = (title) =>
  title.toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();

// Estimate read time from body HTML
export const calcReadTime = (html = '') => {
  const words = html.replace(/<[^>]+>/g, '').split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
};

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const [articles, setArticles] = useState(() => load(LS_ARTICLES, SEED_ARTICLES));
  const [news,     setNews]     = useState(() => load(LS_NEWS,     SEED_NEWS));

  // ── ARTICLES ──────────────────────────────────────────────

  const saveArticles = useCallback((updated) => {
    setArticles(updated);
    save(LS_ARTICLES, updated);
  }, []);

  const addArticle = useCallback((data) => {
    const newItem = {
      ...data,
      id:           Date.now(),
      slug:         toSlug(data.title),
      readTimeMins: calcReadTime(data.body),
      publishedAt:  data.publishedAt || new Date().toISOString().split('T')[0],
      published:    data.published ?? false,
      featured:     data.featured  ?? false,
    };
    saveArticles(prev => {
      const updated = [...prev, newItem];
      save(LS_ARTICLES, updated);
      return updated;
    });
    return newItem;
  }, [saveArticles]);

  const updateArticle = useCallback((id, data) => {
    setArticles(prev => {
      const updated = prev.map(a =>
        a.id === id
          ? { ...a, ...data, slug: toSlug(data.title || a.title), readTimeMins: calcReadTime(data.body || a.body) }
          : a
      );
      save(LS_ARTICLES, updated);
      return updated;
    });
  }, []);

  const deleteArticle = useCallback((id) => {
    setArticles(prev => {
      const updated = prev.filter(a => a.id !== id);
      save(LS_ARTICLES, updated);
      return updated;
    });
  }, []);

  const getArticleBySlug = useCallback((slug) =>
    articles.find(a => a.slug === slug && a.published),
  [articles]);

  const getPublishedArticles = useCallback((categoryId = null) =>
    articles
      .filter(a => a.published && (!categoryId || a.category === categoryId))
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)),
  [articles]);

  const getFeaturedArticles = useCallback(() =>
    articles.filter(a => a.published && a.featured)
      .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)),
  [articles]);

  // ── NEWS ──────────────────────────────────────────────────

  const addNews = useCallback((data) => {
    const newItem = {
      ...data,
      id:        Date.now(),
      slug:      toSlug(data.title),
      date:      data.date || new Date().toISOString().split('T')[0],
      published: data.published ?? false,
      isPinned:  data.isPinned  ?? false,
    };
    setNews(prev => {
      const updated = [...prev, newItem];
      save(LS_NEWS, updated);
      return updated;
    });
    return newItem;
  }, []);

  const updateNews = useCallback((id, data) => {
    setNews(prev => {
      const updated = prev.map(n =>
        n.id === id ? { ...n, ...data, slug: toSlug(data.title || n.title) } : n
      );
      save(LS_NEWS, updated);
      return updated;
    });
  }, []);

  const deleteNews = useCallback((id) => {
    setNews(prev => {
      const updated = prev.filter(n => n.id !== id);
      save(LS_NEWS, updated);
      return updated;
    });
  }, []);

  const getPublishedNews = useCallback(() =>
    news
      .filter(n => n.published)
      .sort((a, b) => {
        if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
        return new Date(b.date) - new Date(a.date);
      }),
  [news]);

  const getNewsBySlug = useCallback((slug) =>
    news.find(n => n.slug === slug && n.published),
  [news]);

  // ── Export / Reset ────────────────────────────────────────

  const exportContent = useCallback(() => {
    const data = JSON.stringify({ articles, news }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const a    = document.createElement('a');
    a.href     = URL.createObjectURL(blob);
    a.download = 'devbrains-content.json';
    a.click();
  }, [articles, news]);

  const resetToSeed = useCallback(() => {
    localStorage.removeItem(LS_ARTICLES);
    localStorage.removeItem(LS_NEWS);
    setArticles(SEED_ARTICLES);
    setNews(SEED_NEWS);
  }, []);

  return (
    <ContentContext.Provider value={{
      // data
      articles, news, CATEGORIES, NEWS_TYPES,
      // article ops
      addArticle, updateArticle, deleteArticle,
      getArticleBySlug, getPublishedArticles, getFeaturedArticles,
      // news ops
      addNews, updateNews, deleteNews,
      getPublishedNews, getNewsBySlug,
      // utils
      exportContent, resetToSeed,
    }}>
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() { return useContext(ContentContext); }
